import { Link, useRoute } from 'wouter'
import clsx from 'clsx'
import { HugeiconsIcon, HugeiconsIconProps } from '@hugeicons/react'

import { CurrentOrganizationButton } from '@/features/organization'
import { CurrentUserButton } from '@/features/user'

interface NavItem {
  id: string
  path: string
  Icon: HugeiconsIconProps['icon']
  label: string
}

interface NavGroup {
  title: string
  items: NavItem[]
}

export function UserItem({
  isSidebarOpen
}: React.PropsWithChildren<{ isSidebarOpen: boolean }>) {
  return (
    <CurrentUserButton
      type={isSidebarOpen ? 'row' : 'image_only'}
      color="white"
    />
  )
}

export function OrganizationItem({
  isSidebarOpen
}: React.PropsWithChildren<{ isSidebarOpen: boolean }>) {
  return (
    <CurrentOrganizationButton
      type={isSidebarOpen ? 'row' : 'image_only'}
      color="white"
    />
  )
}

type ItemGroupProps = NavGroup & { isSidebarOpen: boolean }

export function ItemGroup({ title, items, isSidebarOpen }: ItemGroupProps) {
  if (items.length === 0) return null

  return (
    <menu className="flex flex-col gap-1">
      <h4
        className={clsx(
          'h-6 pl-2 text-regular text-white transition-all whitespace-nowrap',
          !isSidebarOpen && 'opacity-0'
        )}
      >
        {title}
      </h4>
      <div className="flex w-full flex-col gap-1 pl-2.5">
        {items.map(function (item) {
          return (
            <Item key={item.path} {...item} isSidebarOpen={isSidebarOpen} />
          )
        })}
      </div>
    </menu>
  )
}

type ItemProps = NavItem & {
  isSidebarOpen: boolean
  useInternalLink?: boolean
  markAsUnread?: boolean
  target?: HTMLAnchorElement['target']
}

export function Item({
  path,
  Icon,
  label,
  isSidebarOpen,
  useInternalLink = true,
  markAsUnread = false,
  target
}: ItemProps) {
  const [isActive] = useRoute(`${path}/*?`)

  const classes = clsx(
    'relative flex h-11 w-full flex-row items-center gap-3 rounded-l p-3',
    isActive && 'bg-background text-secondary-dark',
    !isActive && 'text-white'
  )

  return (
    <div className="relative flex h-11 w-full">
      {isActive && (
        <div className="absolute right-0 h-full">
          <div className="absolute -top-6 right-0 size-6 bg-background outline-hidden">
            <div className="size-full rounded-br bg-secondary-dark" />
          </div>
          <div className="absolute -bottom-6 right-0 size-6 bg-background">
            <div className="size-full rounded-tr bg-secondary-dark" />
          </div>
        </div>
      )}
      {useInternalLink && (
        <Link className={classes} to={path}>
          <HugeiconsIcon icon={Icon} className="size-4 shrink-0" />
          {markAsUnread && (
            <div className="size-2 absolute bg-primary rounded-full top-3 left-6" />
          )}
          {isSidebarOpen && <h3 className="text-heading3">{label}</h3>}
        </Link>
      )}
      {!useInternalLink && (
        <a className={classes} href={path} target={target}>
          <HugeiconsIcon icon={Icon} className="size-4 shrink-0" />
          {isSidebarOpen && <h3 className="text-heading3">{label}</h3>}
        </a>
      )}
    </div>
  )
}
